import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SetupTask, SetupTasksService } from '../../../../core/services/setup-tasks.service';

const MIN_KEY_PREFIX = 'contemplate.setupBox.minimized.';
const SUCCESS_MS = 3500;

/** Floating "Finish setting up" checklist. Stays (as a pill) while any task is pending. */
@Component({
  selector: 'app-setup-tasks-box',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './setup-tasks-box.component.html',
  styleUrls: ['./setup-tasks-box.component.scss']
})
export class SetupTasksBoxComponent implements OnInit, OnDestroy {
  tasks: SetupTask[] = [];
  minimized = false;
  routeAllowed = true;
  showSuccess = false;
  announcement = '';

  private hadPending = false;
  private successTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private setup: SetupTasksService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.checkRoute();
    this.minimized = this.readMinimized();
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.checkRoute());
    this.setup.tasks$.pipe(takeUntil(this.destroy$)).subscribe((tasks) => this.onTasks(tasks));
  }

  ngOnDestroy(): void {
    if (this.successTimer) clearTimeout(this.successTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  get pendingCount(): number {
    return this.tasks.filter((t) => !t.done).length;
  }

  get doneCount(): number {
    return this.tasks.length - this.pendingCount;
  }

  get visible(): boolean {
    return this.routeAllowed && (this.pendingCount > 0 || this.showSuccess);
  }

  get progressPercent(): number {
    return this.tasks.length ? Math.round((this.doneCount / this.tasks.length) * 100) : 0;
  }

  verify(): void {
    // Every pending task currently shares one dialog; run the first pending task's action.
    this.tasks.find((t) => !t.done)?.action();
  }

  minimize(): void {
    this.setMinimized(true);
  }

  expand(): void {
    this.setMinimized(false);
  }

  trackById(_: number, t: SetupTask): string {
    return t.id;
  }

  private onTasks(tasks: SetupTask[]): void {
    const prev = this.tasks;
    this.tasks = tasks;
    const pending = tasks.filter((t) => !t.done).length;

    const finished = tasks.filter((t) => t.done && prev.some((p) => p.id === t.id && !p.done));
    if (finished.length && pending > 0) {
      this.announcement = `${finished.map((t) => t.label).join(', ')}: complete.`;
    }

    if (pending > 0) {
      this.hadPending = true;
      this.showSuccess = false;
    } else if (this.hadPending && tasks.length) {
      this.hadPending = false;
      this.showSuccess = true;
      this.announcement = 'All set. Your setup is complete.';
      if (this.successTimer) clearTimeout(this.successTimer);
      this.successTimer = setTimeout(() => (this.showSuccess = false), SUCCESS_MS);
    }
  }

  private checkRoute(): void {
    const url = this.router.url.split('?')[0].toLowerCase();
    this.routeAllowed = !(
      url === '/' ||
      url.startsWith('/auth') ||
      url.startsWith('/onboarding') ||
      url.includes('checkout')
    );
  }

  private key(): string {
    return MIN_KEY_PREFIX + (this.setup.userId ?? 'anon');
  }

  private readMinimized(): boolean {
    try {
      return localStorage.getItem(this.key()) === '1';
    } catch {
      return false;
    }
  }

  private setMinimized(value: boolean): void {
    this.minimized = value;
    try {
      localStorage.setItem(this.key(), value ? '1' : '0');
    } catch {
      /* storage unavailable */
    }
  }
}
